import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { translateObject } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { TabObject } from '@inmoment/the-kitchen';

import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { Collection } from 'app/providers/storage-api/collection.service';
import { FieldService, FieldTypeOption } from 'app/providers/storage-api/field.service';
import { Upload, UploadColumn } from 'app/providers/storage-api/upload.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { UploadMapComponent } from '../../pages/upload-map/upload-map.component';

export interface UploadInfoDialogOptions {
  collection: Collection;
  upload: Upload;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-upload-info',
  templateUrl: './upload-info.component.html',
  styleUrls: ['./upload-info.component.scss'],
  standalone: false,
})
export class UploadInfoDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() options: UploadInfoDialogOptions;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any>();
  private ERROR_TYPE_HUMAN_READABLE = {};

  public fieldMap: Map<string, { type: string; label: string }> = new Map();
  public displayedColumnsMapping = ['column_name', 'field_name', 'field_type'];
  public displayedColumnsResults = ['no_rows', 'first_row', 'type', 'details'];
  public uploadColumns: UploadColumn[] = [];
  private groupFieldsSub$: Subscription;
  public uploadInfoTabs: TabObject[] = [];
  public activeTab = 'details';
  private fieldTypeOptions: FieldTypeOption[];

  constructor(
    private blenderAccountService: StorageAccountService,
    private fieldService: FieldService
  ) {}

  ngOnInit() {
    this.fieldTypeOptions = this.fieldService.getFieldTypeOptions();
    const t = translateObject('upload-info');
    Object.defineProperty(this, 'ERROR_TYPE_HUMAN_READABLE', {
      writable: false,
      value: {
        DocumentFieldMissingException: t['missing-field'],
        DocumentTextLengthLimitExceededException: t['text-too-long'],
        DocumentIDLengthLimitExceededException: t['id-too-long'],
        DocumentCountLimitExceededException: t['document-limit-exceeded'],
        CollectionDocumentExistsException: t['document-already-exists'],
      },
    });
    this.uploadInfoTabs = [
      { title: t['details'], id: 'details' },
      { title: t['results'], id: 'results' },
      { title: t['column-mapping'], id: 'columnMapping' },
    ];
    Object.keys(UploadMapComponent.FIELD_NAME_MAP).forEach((key) =>
      this.fieldMap.set(UploadMapComponent.FIELD_NAME_MAP[key], { type: key, label: key })
    );
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.groupFieldsSub$) {
      this.groupFieldsSub$.unsubscribe();
    }
  }

  public get collection(): Collection {
    return this.options.collection;
  }

  public get upload(): Upload {
    return this.options.upload;
  }

  public async refresh(): Promise<void> {
    this.groupFieldsSub$ = this.blenderAccountService.groupFieldsSub.subscribe(
      (accountMetadata) => {
        accountMetadata.metadata.forEach((f) => {
          const fieldType = f.metadata?.rating ? 'rating' : f.fieldType;
          this.fieldMap.set(f.fieldName, {
            type: fieldType,
            label: f.label,
          });
        });
      }
    );

    this.uploadColumns = this.upload.metadata.columns
      .map((column) => {
        if (column.field_name && column.field_name.startsWith('metadata.')) {
          column.field_name = column.field_name.replace('metadata.', '');
        }
        return column;
      })
      .filter((column) => !!column.field_name);
  }

  public getErrorTypeDisplay(value: string): string {
    return this.ERROR_TYPE_HUMAN_READABLE[value] || value;
  }

  public getFieldLabel(name: string): string {
    return this.fieldMap.get(name)?.label;
  }

  // look up the type for a custom field
  public getFieldType(name: string): string {
    if (this.fieldMap.has(name)) {
      const value = this.fieldMap.get(name)?.type;
      for (const opt of this.fieldTypeOptions) {
        if (opt.value === value) {
          return opt.label;
        }
      }
      return value;
    }
    return '';
  }

  cancel(): void {
    this.modalClosed.emit(null);
  }
}
